import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiHelpCircle, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { getFAQs, addFAQ, updateFAQ, deleteFAQ } from '../../firebase/database';
import { Spinner } from '../../components/common/Loader';
import toast from 'react-hot-toast';

const FAQ_CATEGORIES = ['Orders', 'Shipping', 'Returns', 'Products', 'Payment', 'Account', 'General'];

const emptyFAQ = { question: '', answer: '', category: 'General', order: 0 };

const AdminFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyFAQ);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('All');

  useEffect(() => { load(); }, []);
  const load = async () => { setLoading(true); const data = await getFAQs(); setFaqs(data); setLoading(false); };

  const openAdd = () => { setEditing(null); setForm({ ...emptyFAQ, order: faqs.length }); setModal(true); };
  const openEdit = (faq) => { setEditing(faq.id); setForm(faq); setModal(true); };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) { toast.error('Question and answer required'); return; }
    setSaving(true);
    try {
      if (editing) { await updateFAQ(editing, form); toast.success('FAQ updated!'); }
      else { await addFAQ(form); toast.success('FAQ added!'); }
      setModal(false); load();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!id) { toast.error('Invalid FAQ — cannot delete'); return; }
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await deleteFAQ(id);
      toast.success('Deleted');
      load();
    } catch (err) {
      console.error('Delete FAQ failed:', err);
      toast.error('Failed to delete: ' + (err.message || 'Check your admin permissions.'));
    }
  };

  const categories = ['All', ...FAQ_CATEGORIES];
  const filtered = filterCat === 'All' ? faqs : faqs.filter(f => f.category === filterCat);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-black text-gray-900">FAQs Management</h1>
          <p className="text-gray-500 text-sm">{faqs.length} questions · shown on FAQs page</p>
        </div>
        <button onClick={openAdd} className="btn-primary flex items-center gap-2 text-sm">
          <FiPlus className="w-4 h-4" /> Add FAQ
        </button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-5">
        {categories.map(cat => (
          <button key={cat} onClick={() => setFilterCat(cat)} className={`px-3 py-1.5 text-xs font-semibold transition-all ${filterCat === cat ? 'bg-[#1a3a6e] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {cat}
          </button>
        ))}
      </div>

      {/* FAQ List */}
      <div className="bg-white shadow-card overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Spinner /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['#', 'Question', 'Category', 'Answer Preview', 'Actions'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((faq, idx) => (
                <motion.tr key={faq.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-400 font-mono text-xs">{idx + 1}</td>
                  <td className="py-3 px-4 font-medium text-gray-900 max-w-xs">
                    <p className="line-clamp-2">{faq.question}</p>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs font-semibold bg-[#1a3a6e]/10 text-[#1a3a6e] px-2 py-1">{faq.category || 'General'}</span>
                  </td>
                  <td className="py-3 px-4 text-gray-500 text-xs max-w-xs">
                    <p className="line-clamp-2">{faq.answer}</p>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1.5">
                      <button onClick={() => openEdit(faq)} className="p-1.5 text-[#1a3a6e] hover:bg-[#1a3a6e]/10 rounded"><FiEdit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(faq.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><FiTrash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </motion.tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center py-16 text-gray-400">
                  <FiHelpCircle className="w-10 h-10 mx-auto mb-2" />
                  <p>No FAQs {filterCat !== 'All' ? `in ${filterCat}` : 'yet'}. Add your first one!</p>
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {modal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={() => setModal(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-5">
                  <h2 className="font-bold text-lg text-gray-900">{editing ? 'Edit FAQ' : 'Add New FAQ'}</h2>
                  <button onClick={() => setModal(false)} className="p-1.5 hover:bg-gray-100 rounded"><FiX className="w-5 h-5" /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Category</label>
                    <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="input-field">
                      {FAQ_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Question *</label>
                    <input value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} className="input-field" placeholder="What is your question?" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Answer *</label>
                    <textarea rows={5} value={form.answer} onChange={e => setForm(f => ({ ...f, answer: e.target.value }))} className="input-field resize-none" placeholder="Write the answer here..." />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Display Order</label>
                    <input type="number" value={form.order || 0} onChange={e => setForm(f => ({ ...f, order: +e.target.value }))} className="input-field" placeholder="0" />
                    <p className="text-xs text-gray-400 mt-1">Lower number = shown first</p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button onClick={() => setModal(false)} className="btn-outline text-sm py-2 px-5">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="btn-primary text-sm py-2 px-5 flex items-center gap-2">
                    {saving && <Spinner size="sm" color="white" />}
                    {editing ? 'Update FAQ' : 'Add FAQ'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminFAQs;
